import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import useSencoPupils from '../../hooks/useSencoPupils';
import { apiFamilyPathwayConsent } from '../../utils/rmsApi';
import { buildFamilyChild, childFirstName, familyUserName, referralForLine, SERVICE_PRIVACY_NOTICE_URL } from '../../utils/familyPortal';
import { isSecondaryPupil } from '../../utils/harbourPupils';
import { familyConsentVersion } from '../../constants/consentVersions';
import { ProfileSharingChoices, PROFILE_SHARING_QUESTION } from './FamilyProfileSharing';
import FamilyPortalShell from './FamilyPortalShell';
import FamilyChildCard from './FamilyChildCard';
import useIsDesktop from './useIsDesktop';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import '../../components/harbour/harbour.css';
import FamilyLoadingState from './FamilyLoadingState';
import './familyConsent.css';

/*
 * Parent or carer "Confirm consent" page (SEND & ND Access Portal, TDI-53).
 * Route: /rms/family/child/:patientRecordId/consent/:pathway ('sen' | 'referral').
 *
 * Design: Figma UAXOnhR1ZvlsNl4diy00xX — mobile frames 3311:2253 (SEN),
 * 3311:2321 (referral), 3317:4534 (referral · autism), 3317:4595 / 3317:4670 /
 * 3534:19694 (I do not agree); desktop 3449:12744 and siblings. The consent
 * wording below is the agreed first version pending sign-off — copy it
 * exactly, do not reword here.
 *
 * Mobile renders as a plain shell page. Desktop (≥1100) renders the frames'
 * composition: the Your children page behind a scrim, with consent in a wide
 * (1688) task flyout — pathway eyebrow and the child's name in the flyout
 * header, the form card in the task body's main column, the decisions in the
 * card footer, and the "For:" line under the card title.
 *
 * The referral consent is the design's rewritten five-part consent (nodes
 * 4024:28597-4024:28630 + 3311:2393), one shared text for ADHD, autism and
 * combined referrals — only the "For:" line varies — ending in "I agree".
 * A combined referral stays one consent decision on the one referral pathway
 * (mirroring rmsFamilyPathwayConsent, which records a single guardianConsent
 * per pathway).
 */

function senCopy(name, school) {
  return {
    explainer:
      `SEND means special educational needs and disabilities. The SEND pathway helps ${school} understand ${name} ` +
      'and plan the right support in school. It has four steps: Assess, Plan, Do and Review. ' +
      `First, you and the school each answer questions about ${name}. The answers make a profile ` +
      `of ${name}’s strengths and needs. It is about understanding, not labelling.`,
    whoSeesWhat:
      'You and the school answer the questions separately. Neither of you sees the other’s ' +
      `answers. When you have both finished, you can both see ${name}’s profile, with home and ` +
      // TDI-482: a referral no longer shares the profile on its own; the
      // family is asked. Jasmine to confirm this line.
      'school side by side. It stays between you and the school. If you also agree to a ' +
      'referral to an NHS service, you choose whether the hospital team can see it.',
    statements: [
      {
        key: 'sen',
        text:
          `I agree that ${name} can join the SEND pathway at ${school}. I agree that my answers ` +
          `can be used to make ${name}’s profile, and that the school can use the profile to ` +
          `plan ${name}’s support. I can change my mind at any time by telling the school.`,
      },
    ],
  };
}

/*
 * Referral consent (frames 3311:2321 ADHD / 3317:4534 autism, rewritten in the
 * design as one five-part consent shared by both pathways — nodes 4024:28597
 * to 4024:28630 plus the 3311:2393 statement card). The copy is implemented
 * exactly as the frames write it, curly quotes included, and no longer varies
 * by concern or personalises with the child's name: only the "For:" line does.
 * The ADHD/autism split and the TDI-55 autism placeholder are gone.
 */
function ReferralConsentBody() {
  return (
    <>
      <section className="family-consent__section">
        <h2 className="family-consent__section-title">1. What this tool does</h2>
        <p className="family-consent__body">
          This tool asks you some questions about your child, such as their health, behaviour and
          how they are getting on at school.
        </p>
        <p className="family-consent__body">
          Your answers will help clinicians make sense of what your child needs. They also help
          them decide what kind of help your child might need next.
        </p>
      </section>

      <section className="family-consent__section">
        <h2 className="family-consent__section-title">2. What this tool does not do</h2>
        <p className="family-consent__body">
          This tool does not make any decisions about your child’s care.
        </p>
        <p className="family-consent__body">
          It does not tell you if your child has autism or ADHD. Only clinicians can do that.
        </p>
        <p className="family-consent__body">
          Clinicians will read the answers from you and your child’s school. They will decide what
          happens next.
        </p>
        <p className="family-consent__body">
          Answering the questions in this tool does not mean that your child will get an assessment
          or any kind of support.
        </p>
      </section>

      <section className="family-consent__section">
        <h2 className="family-consent__section-title">3. How we use your details</h2>
        <p className="family-consent__body">
          If you agree to take part, we will share this information with the clinic team:
        </p>
        <ul className="family-consent__list family-consent__body">
          <li><strong>Their full name</strong></li>
          <li><strong>Their date of birth</strong></li>
          <li><strong>Their NHS number</strong> (if you know it)</li>
          <li><strong>Their address</strong></li>
          <li><strong>Their school or college name</strong></li>
          <li><strong>Your contact details</strong> (phone number and email)</li>
          <li><strong>Their GP surgery</strong></li>
          <li>
            <strong>All of your answers in this form</strong> (including what you tell us about
            their health, behaviour, and daily life)
          </li>
        </ul>
        <p className="family-consent__body">
          We will keep your details safe and use them in line with data protection law.
        </p>
        <p className="family-consent__body">
          We use your answers to help your child. We may need to share your information with people
          helping your child like doctors, teachers, or social workers. We only share details that
          matter for your child’s care.
        </p>
        <p className="family-consent__body">
          Sometimes, we must share details by law. This is normally to keep someone safe from harm.
        </p>
      </section>

      <section className="family-consent__section">
        <h2 className="family-consent__section-title">4. Your privacy</h2>
        <p className="family-consent__body">
          {/* A new tab, so the family keeps their place in the consent. */}
          Please read the Trust’s{' '}
          <a
            className="family-consent__link"
            href={SERVICE_PRIVACY_NOTICE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Notice (opens in a new tab)
          </a>{' '}
          to find out more about how your information is used and stored.
        </p>
        <p className="family-consent__body">
          You can ask to see the details we hold about you. You can also ask us to change it if it
          is wrong. To do this, get in touch with the Information Rights Team:
        </p>
        <ul className="family-consent__list family-consent__body">
          <li>
            <strong>Email:</strong>{' '}
            <a className="family-consent__link" href="mailto:bfwh.information.rights@nhs.net">
              bfwh.information.rights@nhs.net
            </a>
          </li>
          <li>
            <strong>Phone:</strong>{' '}
            <a className="family-consent__link" href="tel:01253958105">01253 958105</a>
          </li>
        </ul>
        <p className="family-consent__body">
          We only keep your information for as long as we need it, in line with the Trust’s
          retention schedule.
        </p>
        <p className="family-consent__body">Have questions about your data? You can:</p>
        <ul className="family-consent__list family-consent__body">
          <li><strong>Ask the person who referred your child, or</strong></li>
          <li>
            <strong>Contact our team:</strong>
            <ul>
              <li>
                <strong>Email:</strong>{' '}
                <a
                  className="family-consent__link"
                  href="mailto:bfwh.information.governance@nhs.net"
                >
                  bfwh.information.governance@nhs.net
                </a>
              </li>
              <li>
                <strong>Phone:</strong>{' '}
                <a className="family-consent__link" href="tel:01253953057">01253 953057</a>
              </li>
            </ul>
          </li>
        </ul>
      </section>

    </>
  );
}

/** "5. Going ahead": the referral's statement, on the deciding step (Q-1). */
function GoingAheadStatement() {
  return (
    <div className="family-consent__statement">
      <h2 className="family-consent__statement-title">5. Going ahead</h2>
      <p>By clicking “I agree,” you are saying that you have read this and are happy to go ahead.</p>
    </div>
  );
}

/*
 * The profile sharing question (TDI-482, frames Q-1 to Q-3): asked on the
 * deciding step of the referral consent. Nothing chosen goes ahead as No,
 * and the note says so, so "I agree" is never blocked by it.
 */
function ProfileQuestion({ name, value, onChange }) {
  return (
    <section className="family-consent__section family-consent__profile-question" aria-label="Profile sharing">
      <h2 className="family-consent__section-title">{PROFILE_SHARING_QUESTION(name)}</h2>
      <p className="family-consent__body family-consent__profile-help">
        The team reviewing the referral can use it to understand what {name} finds easy and hard.
        It&rsquo;s your choice.
      </p>
      <ProfileSharingChoices value={value} onChange={onChange} name="profile-sharing-consent" />
      {!value && (
        <HarbourNotice kind="info" title="If you don’t choose, the hospital team won’t see it." />
      )}
    </section>
  );
}

export default function FamilyConsentPage() {
  const navigate = useNavigate();
  const { patientRecordId, pathway } = useParams();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  const { pupils, loading, error, reload } = useSencoPupils({ orgId, claimsOrgId });
  const isDesktop = useIsDesktop();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // 'agree' | 'disagree' | null
  // The referral consent is two steps (Q-0 read, Q-1 decide): the profile
  // sharing question sits on the deciding step with the decision itself.
  const [step, setStep] = useState(1);
  const [profileSharing, setProfileSharing] = useState(null); // 'yes' | 'no' | null
  const [submitFailed, setSubmitFailed] = useState(false);
  const dialogTitleRef = useRef(null);

  const childUrl = `/rms/family/child/${patientRecordId}`;
  const busy = Boolean(busyAction);

  const closeConfirm = useCallback(() => {
    if (!busyAction) setConfirmOpen(false);
  }, [busyAction]);

  // The confirm sheet is a dialog: focus moves to its title, Escape closes it.
  useEffect(() => {
    if (!confirmOpen) return undefined;
    if (dialogTitleRef.current) dialogTitleRef.current.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeConfirm();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [confirmOpen, closeConfirm]);

  // Only the child's parent or carer may answer a consent question.
  if (claims && claims.userType !== 'RMS Legal Guardian') {
    return <Navigate to="/rms/dashboard" replace />;
  }
  if (pathway !== 'sen' && pathway !== 'referral') {
    return <Navigate to={childUrl} replace />;
  }

  const pupil = (pupils || []).find((p) => p.id === patientRecordId) || null;
  const patient = pupil?.patient || {};
  const pathwayData = pathway === 'sen' ? patient.senPathway : patient.referralPathway;

  // Already answered: never re-ask — back to the child's page.
  if (pupil && pathwayData?.guardianConsent?.decision) {
    return <Navigate to={childUrl} replace />;
  }

  const userName = familyUserName(user, pupils) || 'Account';
  const name = childFirstName(pupil) || 'your child';
  const school = patient.school?.schoolName || patient.schoolName || 'their school';
  const forLine = pathway === 'referral' ? referralForLine(patient.intakeConcern) : null;
  const copy = pathway === 'sen' ? senCopy(name, school) : null;

  const submit = async (decision) => {
    setBusyAction(decision);
    setSubmitFailed(false);
    try {
      // Record which wording was on this screen (TDI-480).
      await apiFamilyPathwayConsent({
        orgId, patientRecordId, pathway, decision,
        consentVersion: familyConsentVersion(pathway),
        // The profile sharing answer travels with a referral agree
        // (TDI-482); nothing chosen is recorded by the server as No.
        ...(pathway === 'referral' && decision === 'agree' ? { profileSharing } : {}),
      });
      navigate(decision === 'agree' ? `${childUrl}/forms/${pathway}` : childUrl);
    } catch (e) {
      setBusyAction(null);
      setConfirmOpen(false);
      // A refusal is not a connection problem: do not tell the parent to
      // check their internet when the platform said no.
      setSubmitFailed(String(e?.code || '').includes('permission-denied') ? 'refused' : 'network');
    }
  };

  const shell = (children) => (
    <FamilyPortalShell userName={userName} back={{ to: childUrl, label: 'Back' }}>
      {children}
    </FamilyPortalShell>
  );

  if (loading) {
    return shell(
      <FamilyLoadingState />
    );
  }

  if (!pupil) {
    return shell(
      <div className="family-consent__load-error">
        <HarbourNotice kind="error" title="We could not load this page">
          {error || 'Check your internet connection and try again.'}
        </HarbourNotice>
        <HarbourButton kind="secondary" onClick={() => reload({ force: true })}>
          Try again
        </HarbourButton>
      </div>
    );
  }

  // Everything under the page head is the same on both widths: explainer,
  // who sees what, the statements, the failure notice and the two decisions.
  const referralReading = pathway === 'referral' && step === 1;
  const consentBody = referralReading ? (
    <>
      <ReferralConsentBody />
      <div className="family-consent__actions">
        <HarbourButton
          kind="family"
          className="family-consent__action"
          onClick={() => setStep(2)}
        >
          Continue
        </HarbourButton>
      </div>
    </>
  ) : (
    <>
      {pathway === 'referral' ? (
        <>
          {/* Secondary pupils never do the HFP (TD-129/TD-137), so there is
              no profile to ask about. */}
          {!isSecondaryPupil(pupil) && (
            <ProfileQuestion name={name} value={profileSharing} onChange={setProfileSharing} />
          )}
          <GoingAheadStatement />
        </>
      ) : (
        <>
          <p className="family-consent__body">{copy.explainer}</p>

          <section className="family-consent__section">
            <h2 className="family-consent__section-title">Who sees what</h2>
            <p className="family-consent__body">{copy.whoSeesWhat}</p>
          </section>

          {copy.statements.map((statement) => (
            <div key={statement.key} className="family-consent__statement">
              <p>{statement.text}</p>
            </div>
          ))}
        </>
      )}

      {submitFailed && (
        <HarbourNotice kind="error" title="We could not save your answer">
          {submitFailed === 'refused'
            ? `Your answer has not been recorded. Please contact ${school} so they can check your details.`
            : 'Check your internet connection and try again. Your answer has not been recorded.'}
        </HarbourNotice>
      )}

      <div className="family-consent__actions">
        <HarbourButton
          kind="secondary"
          className="family-consent__action"
          disabled={busy}
          onClick={() => setConfirmOpen(true)}
        >
          I do not agree
        </HarbourButton>
        <HarbourButton
          kind="family"
          className="family-consent__action"
          busy={busyAction === 'agree'}
          disabled={busy}
          aria-busy={busyAction === 'agree'}
          onClick={() => submit('agree')}
        >
          {pathway === 'referral' ? 'I agree' : 'Confirm consent'}
        </HarbourButton>
      </div>
    </>
  );

  const confirmSheet = confirmOpen && (
    <div
      className="family-consent-scrim"
      onClick={closeConfirm}
      data-testid="family-consent-scrim"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="family-consent-dialog-title"
        className="family-consent-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="family-consent-sheet__grabber" aria-hidden="true" />
        <h2
          id="family-consent-dialog-title"
          ref={dialogTitleRef}
          tabIndex={-1}
          className="family-consent-sheet__title"
        >
          Are you sure you do not agree?
        </h2>
        <p className="family-consent__body">
          If you do not agree, the school cannot start this pathway for {name}.
        </p>
        <div className="family-consent-sheet__actions">
          <HarbourButton
            kind="secondary"
            className="family-consent__action"
            disabled={busy}
            onClick={closeConfirm}
          >
            Cancel
          </HarbourButton>
          <HarbourButton
            kind="primary"
            className="family-consent__action"
            busy={busyAction === 'disagree'}
            disabled={busy}
            aria-busy={busyAction === 'disagree'}
            onClick={() => submit('disagree')}
          >
            I do not agree
          </HarbourButton>
        </div>
      </div>
    </div>
  );

  // Desktop: the task flyout over Your children (3449:12744 and siblings).
  if (isDesktop) {
    const allChildren = (pupils || []).map(buildFamilyChild).filter(Boolean);
    // The flyout's Close (and Escape) first dismisses the confirm sheet if it
    // is open, and never navigates while a decision is being recorded.
    const closeFlyout = () => {
      if (busy) return;
      if (confirmOpen) {
        setConfirmOpen(false);
        return;
      }
      navigate(childUrl, { state: { stay: true } });
    };
    return (
      <FamilyPortalShell userName={userName} maxWidth="full">
        <div className="family-page-head">
          <h1 className="family-page-head__title family-page-head__title--home">Your children</h1>
          <p className="family-page-head__intro">Choose your child’s name to see their details and questions.</p>
        </div>
        <ul className="family-child-cards">
          {allChildren.map((c) => (
            <li key={c.id}><FamilyChildCard child={c} linkState={{ stay: true }} /></li>
          ))}
        </ul>

        <HarbourFlyout
          open
          width="wide"
          title={pupil.name}
          year={pupil.yearGroup}
          eyebrow={{ tone: pathway, label: pathway === 'sen' ? 'SEND pathway' : 'Referral pathway' }}
          onClose={closeFlyout}
          closeLabel="Close"
        >
          <div className="harbour-task-body">
            <div className="harbour-task-body__steps" aria-hidden="true" />
            <div className="harbour-task-body__main">
              <div className={`family-consent family-consent--${pathway}`}>
                <header className="family-consent__head">
                  <h2 className="family-consent__title">Confirm consent</h2>
                  {forLine && <p className="family-consent__for">{forLine}</p>}
                </header>
                {consentBody}
              </div>
            </div>
          </div>
          {confirmSheet}
        </HarbourFlyout>
      </FamilyPortalShell>
    );
  }

  // Mobile: the plain shell page (3311:2253 and siblings).
  return shell(
    <>
      <div className={`family-consent family-consent--${pathway}`}>
        <header className="family-consent__head">
          <p className="family-consent__eyebrow">
            <HarbourPathwayMark pathway={pathway} />
            <span>{pathway === 'sen' ? 'SEND pathway' : 'Referral pathway'}</span>
          </p>
          {forLine && <p className="family-consent__for">{forLine}</p>}
          <h1 className="family-consent__title">Confirm consent</h1>
        </header>
        {consentBody}
      </div>
      {confirmSheet}
    </>
  );
}
